import { Button, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useCurrentAssetWallet } from 'src/Hooks/Shared/useCurrentAssetWallet';
import useOperationActions from 'src/Hooks/Shared/useOperationActions';
import { SettlementTargetDto, SettlementTargetDtoTypeEnum } from 'src/Services/API/generated';
import { formatEuros } from 'src/Utils/formatEuros';

import styles from './BalanceCard.module.scss';
import { Props } from './BalanceCard.types';
import { DashboardCard } from './DashboardCard';

/** Above this count the subline is likely to be truncated, so it gets a tooltip. */
const NAMES_TOOLTIP_THRESHOLD = 2;

const formatShortName = (target: SettlementTargetDto): string =>
  target.type === SettlementTargetDtoTypeEnum.BankAccount
    ? (target.name ?? '')
    : (target.firstName ?? target.lastName ?? '');

const BalanceCard = ({ onShowDetail }: Props) => {
  const { t } = useTranslation();
  // Mono-asset invariant: never surface `myTotalNetBalance`, it spans every asset.
  const { netBalance, owedToMeBy, iOweTo, isSettled, isLoading, isError, refetch } =
    useCurrentAssetWallet();
  // Same drawer as "Enregistrer un remboursement" on the operations page.
  const { addTransfer } = useOperationActions('home');

  const isDebt = !isSettled && netBalance < 0;
  const counterparties = netBalance > 0 ? owedToMeBy : iOweTo;
  const names = counterparties.map(entry => formatShortName(entry.target)).filter(Boolean);

  const joinNames = (): string => {
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} ${t('common.and', 'et')} ${names[names.length - 1]}`;
  };

  const sublineText = isSettled
    ? ''
    : isDebt
      ? t('expenses.dashboard.toList', 'à {{names}}', { names: joinNames() })
      : t('expenses.dashboard.fromList', 'par {{names}}', { names: joinNames() });

  const figure = isSettled ? (
    <span className={styles.settled}>{t('expenses.dashboard.settled', 'Vous êtes à jour')}</span>
  ) : (
    <>
      {/* Colour is never the only carrier of meaning: the words say it too. */}
      <span className={styles.word}>
        {isDebt ? t('expenses.youOwe', 'Vous devez') : t('expenses.youAreOwed', 'On vous doit')}
      </span>
      <span className={isDebt ? styles.debt : styles.credit}>
        {formatEuros(Math.abs(netBalance))}
      </span>
    </>
  );

  const subline =
    sublineText.length > 0 ? (
      names.length > NAMES_TOOLTIP_THRESHOLD ? (
        <Tooltip label={sublineText} multiline w={280}>
          <span>{sublineText}</span>
        </Tooltip>
      ) : (
        <span>{sublineText}</span>
      )
    ) : undefined;

  return (
    <DashboardCard
      label={t('mySoldCard.title', 'Mon solde')}
      figure={figure}
      subline={subline}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      actions={
        <>
          <Button size="sm" onClick={onShowDetail}>
            {t('mySoldCard.seeDetail', 'Voir le détail')}
          </Button>
          {isDebt && (
            <Button size="sm" variant="light" onClick={addTransfer}>
              {t('expenses.dashboard.reimburse', 'Rembourser')}
            </Button>
          )}
        </>
      }
    />
  );
};

export default BalanceCard;
